import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from './UserContext';
import { getApiUrl } from '@/lib/api-config';
import { isNavKey, type NavKey } from '@/lib/navigation-catalog';

/**
 * What the signed-in person's ROLE may open IN THE ACTIVE COMPANY (GET /api/me/navigation): menus, submenus, tabs and routes, by stable key
 * (lib/navigation-catalog.ts). The server resolves the role itself and answers with the keys that are ON for it; nothing here chooses a role.
 *
 * NO FALLBACK. Until the answer for THIS company and THIS role has arrived nothing is visible (`isVisible` is false for every key), and if it cannot be read the
 * status is 'error' -- the app shows a retry, never the modules "by default". An answer for another company or role is never reused: the status is 'loaded'
 * only for the exact (company, role) pair it was requested for.
 *
 * Visibility is a ceiling, not an authorization: permissions, RLS, membership and assignment still decide every record (and the server refuses the API of a module that is OFF).
 */
export type NavigationStatus = 'loading' | 'loaded' | 'error';

type NavigationValue = {
  status: NavigationStatus;
  /** true only when the matrix of the active company + role has been loaded and this key is ON (with every ancestor) */
  isVisible: (key: NavKey) => boolean;
  /** the same, for a key that arrives as text (a path-derived key); unknown keys are never visible */
  isVisibleKey: (key: string) => boolean;
  retry: () => void;
};

// The default is the closed state: a component rendered without the provider sees nothing.
const closed: NavigationValue = { status: 'loading', isVisible: () => false, isVisibleKey: () => false, retry: () => undefined };
const NavigationContext = createContext<NavigationValue>(closed);

type Loaded = { scope: string; keys: Set<string> };

const REFRESH_MS = 60_000;

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const { companyId, userRole } = useUser();
  const scope = companyId && userRole ? `${companyId}:${userRole}` : null;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failedScope, setFailedScope] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!scope || !companyId) return undefined;
    const request = ++requestRef.current;
    (async () => {
      try {
        const response = await fetch(getApiUrl('api/me/navigation'));
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success || !Array.isArray(result.data?.keys)) throw new Error(`navigation unavailable (${response.status})`);
        // the answer must be for the company and the role this screen is showing; anything else is not used
        if (result.data.company_id !== companyId || result.data.role !== userRole) throw new Error('navigation answered for another company or role');
        if (request !== requestRef.current) return; // a newer request (another company) superseded this one
        setFailedScope(null);
        setLoaded({ scope, keys: new Set<string>(result.data.keys.filter((key: unknown): key is string => typeof key === 'string' && isNavKey(key))) });
      } catch (error) {
        if (request !== requestRef.current) return;
        console.error('Could not load the navigation of the current company:', error);
        setFailedScope(scope);
      }
    })();
    return () => { requestRef.current++; };
  }, [scope, companyId, userRole, attempt]);

  const retry = useCallback(() => { setLoaded(null); setFailedScope(null); setAttempt((n) => n + 1); }, []);

  // The matrix is edited in the database, and the server reads it on every request. Re-read it when the person comes back to the tab and every REFRESH_MS while it is
  // visible, so an edit reaches an open session without a reload. A failed re-read turns into the error state (never into the previous answer).
  useEffect(() => {
    if (!scope) return undefined;
    const refresh = () => { if (document.visibilityState === 'visible') setAttempt((n) => n + 1); };
    const timer = window.setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [scope]);

  const value = useMemo<NavigationValue>(() => {
    const ready = !!scope && loaded !== null && loaded.scope === scope; // never another company's / role's answer
    const keys = ready && !(!!scope && failedScope === scope) ? loaded.keys : null; // a failed re-read closes everything, it does not keep the old answer
    const failed = !!scope && failedScope === scope;
    const status: NavigationStatus = failed ? 'error' : ready ? 'loaded' : 'loading';
    const visible = (key: string) => keys !== null && keys.has(key);
    return { status, isVisible: (key) => visible(key), isVisibleKey: (key) => isNavKey(key) && visible(key), retry };
  }, [scope, loaded, failedScope, retry]);

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export const useNavigation = () => useContext(NavigationContext);
